import { Button } from '@chakra-ui/button';
import { Checkbox } from '@chakra-ui/checkbox';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/form-control';
import { Input } from '@chakra-ui/input';
import { Heading, Wrap, WrapItem } from '@chakra-ui/layout';
import { MenuButton } from '@chakra-ui/menu';
import {
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
} from '@chakra-ui/number-input';
import { Textarea } from '@chakra-ui/textarea';
import moment from 'moment';
import React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { Datepicker } from '../../../../components/styled-chakra/Datepicker';
import { Menu } from '../../../../components/styled-chakra/Menu';
import { useConfig } from '../../../../hooks/useConfig';
import { AdminCoursesModalForm } from '../../../../types/types';

interface AdminCoursesModalCommonProps {
  form: UseFormReturn<AdminCoursesModalForm>;
}

export const AdminCoursesModalCommon: React.FC<AdminCoursesModalCommonProps> =
  ({ form }) => {
    const firebaseConfig = useConfig();
    const { errors } = form.formState;
    const canRegister = form.watch('canRegister');
    const isOnline = form.watch('isOnline');

    return (
      <section>
        <Heading mb={6} size="sm">
          Allgemeine Informationen
        </Heading>
        <FormControl isRequired isInvalid={!!errors.title} mb={6}>
          <FormLabel>Titel</FormLabel>
          <Input
            aria-invalid={errors.title ? 'true' : 'false'}
            maxLength={30}
            focusBorderColor="brand.500"
            type="text"
            placeholder="Mein Affengeiler Kurs"
            {...form.register('title', { required: true, maxLength: 30 })}
          />
        </FormControl>
        <FormControl isRequired isInvalid={!!errors.description} mb={6}>
          <FormLabel>Beschreibung</FormLabel>
          <Textarea
            focusBorderColor="brand.500"
            resize="vertical"
            maxLength={500}
            placeholder="Eine kurze Beschreibung des Kurses"
            {...form.register('description', {
              required: true,
              maxLength: 500,
            })}
          />
        </FormControl>
        <FormControl mt={3} isInvalid={!!errors.isOnline} mb={9}>
          <FormLabel>Online-Kurs</FormLabel>
          <Checkbox
            value={isOnline ? '1' : '0'}
            type="checkbox"
            {...form.register('isOnline')}
          >
            Ja, dieser Kurs findet online statt
          </Checkbox>
        </FormControl>
        <FormControl mt={3} isInvalid={!!errors.isOnline} mb={9}>
          <FormLabel>Studio-Registrierungen</FormLabel>

          <Checkbox
            value={form.watch().canRegister ? '1' : '0'}
            type="checkbox"
            {...form.register('canRegister')}
          >
            Ja, für diesen Kurs kann eine Studio-Registrierung vorgenommen
            werden
          </Checkbox>

          {canRegister && (
            <FormControl mt={3}>
              <FormLabel>maximal mögliche Registrierungen</FormLabel>
              <NumberInput
                isRequired={form.watch().canRegister}
                focusBorderColor="brand.500"
                step={1}
                defaultValue={10}
                min={1}
                max={999}
              >
                <NumberInputField
                  {...form.register('maxParticipants', {
                    required: form.watch().canRegister,
                  })}
                />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          )}
        </FormControl>
        <Wrap spacing="1em">
          <WrapItem flex={1}>
            <FormControl isRequired isInvalid={!!errors.startDate} mb={6}>
              <FormLabel>Startdatum</FormLabel>
              <Controller
                name="startDate"
                control={form.control}
                render={(f) => (
                  <Menu
                    menuProps={{ closeOnSelect: true }}
                    triggerComponent={
                      <MenuButton
                        borderColor={errors.startDate ? 'red' : 'inherit'}
                        onBlur={f.field.onBlur}
                        as={Button}
                        variant="outline"
                        fontWeight="normal"
                        textAlign="left"
                        w="full"
                      >
                        {moment(f.field.value).format('L')}
                      </MenuButton>
                    }
                  >
                    <Datepicker
                      value={moment(f.field.value)}
                      closeOnSelect={true}
                      p={2}
                      input={false}
                      locale="de"
                      timeFormat={false}
                      onChange={(v) => {
                        f.field.onChange(v);
                      }}
                      on
                    />
                  </Menu>
                )}
              />
            </FormControl>
          </WrapItem>
          <WrapItem flex={1}>
            <FormControl isRequired isInvalid={!!errors.startTime} mb={6}>
              <FormLabel>Startzeit</FormLabel>
              <Input
                focusBorderColor="brand.500"
                type="time"
                placeholder="00:00"
                {...form.register('startTime', {
                  required: true,
                  pattern: /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/,
                })}
              />
              <FormErrorMessage>
                {errors.startTime &&
                  'Überprüfe das Format. Es sollte "00:00" sein'}
              </FormErrorMessage>
            </FormControl>
          </WrapItem>
        </Wrap>
        <Wrap spacing="1em">
          <WrapItem flex={1}>
            <FormControl isRequired isInvalid={!!errors.duration} mb={6}>
              <FormLabel>Dauer (in min)</FormLabel>
              <NumberInput
                focusBorderColor="brand.500"
                step={5}
                defaultValue={30}
                min={5}
                max={300}
              >
                <NumberInputField
                  {...form.register('duration', { required: true })}
                />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          </WrapItem>
          <WrapItem flex={1}>
            <FormControl isRequired isInvalid={!!errors.trainerId} mb={6}>
              <FormLabel>Trainer/in</FormLabel>
              <Input
                focusBorderColor="brand.500"
                type="text"
                placeholder="Max Mustermann"
                {...form.register('trainerId', {
                  required: true,
                })}
              />
            </FormControl>
          </WrapItem>
          <WrapItem flex={1}>
            <FormControl isRequired isInvalid={!!errors.roomName} mb={6}>
              <FormLabel>Raum</FormLabel>
              <Input
                focusBorderColor="brand.500"
                type="text"
                placeholder="Raum 1"
                {...form.register('roomName', {
                  required: true,
                })}
              />
            </FormControl>
          </WrapItem>
        </Wrap>
      </section>
    );
  };
